import { Fragment, useMemo } from "react";
import { GdRichTextInline, GdRichTextParagraphs } from "@/components/ui/GdRichText";
import {
  calcDocument,
  formatCurrency,
  formatQuantityWithUnit,
  groupTaxByRate,
  isExcludedFromDocumentTotal,
  documentRabattLabel,
  positionEpNetto,
} from "@/lib/documentCalc";
import {
  getDokumentTypLabel,
  buildLeistungempfaengerLine,
  buildRecipientAddressLines,
  dokumentDisplayNumber,
  effectiveBelegdatum,
  formatBelegdatum,
  type DokumentDetail,
} from "@/lib/dokumente";
import { resolveGdPlaceholders } from "@/lib/gdPlaceholders";
import { supplyDateSummary } from "@/lib/leistungsdatum";
import { buildDocumentPositionLayout } from "@/pages/dokumente/documentPositionLayout";
import { DEFAULT_NACHWEIS_HINWEIS, readLieferscheinDelivery } from "@/pages/dokumente/gdPositionMeta";
import {
  getNachweisHinweis,
  isNachweisPosition,
  normalizePositionTyp,
  positionDisplayDescription,
  positionDisplayTitle,
  positionPositionsartBadgeLabel,
  positionTotals,
  syncLeistungEinzelpreis,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";

interface DocumentPreviewPanelProps {
  doc: DokumentDetail;
  positions: EditorPosition[];
  embedded?: boolean;
}


export function DocumentPreviewPanel({ doc, positions, embedded = false }: DocumentPreviewPanelProps) {
  const totals = useMemo(() => calcDocument(positions), [positions]);
  const taxGroups = useMemo(() => groupTaxByRate(positions), [positions]);
  const addressLines = useMemo(() => buildRecipientAddressLines(doc), [doc]);
  const leistungempfaengerLine = useMemo(() => buildLeistungempfaengerLine(doc), [doc]);
  const layout = useMemo(() => buildDocumentPositionLayout(positions), [positions]);
  const isLieferschein = doc.dokument_typ === "lieferschein";
  const showPrices = !isLieferschein;
  const labelColSpan = showPrices || isLieferschein ? 3 : 1;
  const belegdatum = effectiveBelegdatum(doc);
  const supplyLine = supplyDateSummary(doc, belegdatum);
  const previewDoc = useMemo(
    () => ({
      ...doc,
      belegdatum,
      summe_netto: totals.summe_netto,
      summe_steuer: totals.summe_steuer,
      summe_brutto: totals.summe_brutto,
    }),
    [doc, belegdatum, totals],
  );
  const resolvedEinleitung = useMemo(
    () => resolveGdPlaceholders(doc.einleitung, previewDoc),
    [doc.einleitung, previewDoc],
  );
  const resolvedSchlusstext = useMemo(
    () => resolveGdPlaceholders(doc.schlusstext, previewDoc),
    [doc.schlusstext, previewDoc],
  );

  return (
    <aside className={embedded ? undefined : "gd-preview-shell"}>
      {!embedded ? <p className="gd-preview-shell__title">Vorschau</p> : null}
      <div className="gd-preview-paper">
        <header className="gd-preview-paper__header">
          <div>
            <p className="gd-preview-paper__doctype">{getDokumentTypLabel(doc)}</p>
            <p className="gd-preview-paper__number">{dokumentDisplayNumber(doc)}</p>
          </div>
          <div className="gd-preview-paper__meta">
            {belegdatum ? (
              <p>
                <span>Datum:</span> {formatBelegdatum(belegdatum)}
              </p>
            ) : null}
            {doc.gueltig_bis ? (
              <p>
                <span>Gültig bis:</span> {formatBelegdatum(doc.gueltig_bis)}
              </p>
            ) : null}
            {supplyLine ? <p>{supplyLine}</p> : null}
          </div>
        </header>

        {addressLines.length || doc.kundennummer?.trim() ? (
          <section className="gd-preview-paper__address">
            {addressLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {doc.kundennummer?.trim() ? (
              <p>Kundennummer: {doc.kundennummer.trim()}</p>
            ) : null}
          </section>
        ) : null}

        {leistungempfaengerLine ? (
          <p className="gd-preview-paper__leistungempfaenger">{leistungempfaengerLine}</p>
        ) : null}

        {doc.betreff ? (
          <h2 className="gd-preview-paper__subject">
            <GdRichTextInline text={doc.betreff} />
          </h2>
        ) : null}

        {resolvedEinleitung ? (
          <GdRichTextParagraphs text={resolvedEinleitung} className="gd-preview-paper__intro" />
        ) : null}

        <table
          className={
            isLieferschein
              ? "gd-preview-table gd-preview-table--lieferschein"
              : showPrices
                ? "gd-preview-table"
                : "gd-preview-table gd-preview-table--no-prices"
          }
        >
          <colgroup>
            <col className="col-pos" />
            <col className="col-menge" />
            <col className="col-bezeichnung" />
            {showPrices ? (
              <>
                <col className="col-ep" />
                <col className="col-gesamt" />
              </>
            ) : null}
            {isLieferschein ? (
              <>
                <col className="col-geliefert" />
                <col className="col-rueckstand" />
              </>
            ) : null}
          </colgroup>
          <thead>
            <tr>
              <th>Pos.</th>
              <th className="gd-preview-table__num">Menge</th>
              <th>Bezeichnung</th>
              {showPrices ? (
                <>
                  <th className="gd-preview-table__num">EP netto</th>
                  <th className="gd-preview-table__num">Gesamt netto</th>
                </>
              ) : null}
              {isLieferschein ? (
                <>
                  <th className="gd-preview-table__num">Geliefert</th>
                  <th className="gd-preview-table__num">Rückstand</th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {layout.map((item, layoutIndex) => {
              if (item.type === "titelsumme") {
                if (!showPrices) return null;
                return (
                  <tr key={`titelsumme-${item.titelLocalId}-${layoutIndex}`} className="gd-preview-table__titelsumme">
                    <td />
                    <td />
                    <td>
                      <strong>Titelsumme {item.titelTitle}</strong>
                    </td>
                    <td />
                    <td className="gd-preview-table__num">
                      {formatCurrency(item.sumNetto, doc.waehrung)}
                    </td>
                  </tr>
                );
              }

              const row = positions[item.index];
              if (!row) return null;
              const typ = normalizePositionTyp(row.position_typ);
              const synced = typ === "leistung" ? syncLeistungEinzelpreis(row) : row;
              const title = positionDisplayTitle(synced);
              const description = positionDisplayDescription(synced);
              const indentClass = item.indented ? "gd-preview-table__unterposition" : undefined;

              if (typ === "titel") {
                const titelBeschreibung = (row.beschreibung || "").trim();
                return (
                  <tr key={row.localId} className="gd-preview-table__titel">
                    <td>{item.displayNumber ?? ""}</td>
                    <td />
                    <td colSpan={labelColSpan}>
                      <strong>{title}</strong>
                      {titelBeschreibung ? (
                        <p className="gd-preview-table__desc">
                          <GdRichTextInline text={titelBeschreibung} />
                        </p>
                      ) : null}
                    </td>
                  </tr>
                );
              }

              if (typ === "text") {
                const textBeschreibung = (row.beschreibung || "").trim();
                return (
                  <tr key={row.localId} className={indentClass ? `gd-preview-table__text ${indentClass}` : "gd-preview-table__text"}>
                    <td />
                    <td />
                    <td colSpan={labelColSpan}>
                      {title !== "Textblock" ? <strong>{title}</strong> : null}
                      {textBeschreibung ? (
                        <p className="gd-preview-table__desc">
                          <GdRichTextInline text={textBeschreibung} />
                        </p>
                      ) : null}
                    </td>
                  </tr>
                );
              }

              const lineTotals = positionTotals(synced);
              const positionsartBadge = positionPositionsartBadgeLabel(synced);
              const excludedFromTotal = isExcludedFromDocumentTotal(synced);
              const delivery = isLieferschein ? readLieferscheinDelivery(synced) : null;
              let nachweisHinweis = isNachweisPosition(synced) ? getNachweisHinweis(synced) : "";
              if (nachweisHinweis && !showPrices && nachweisHinweis === DEFAULT_NACHWEIS_HINWEIS) {
                nachweisHinweis = "Die Arbeiten werden auf Nachweis durchgeführt.";
              }
              return (
                <Fragment key={row.localId}>
                  {nachweisHinweis ? (
                    <tr className="gd-preview-table__hinweis-row">
                      <td />
                      <td />
                      <td colSpan={labelColSpan}>
                        <p className="gd-preview-table__nachweis-hinweis">{nachweisHinweis}</p>
                      </td>
                    </tr>
                  ) : null}
                  <tr className={indentClass}>
                    <td>{item.displayNumber ?? ""}</td>
                    <td className="gd-preview-table__num">
                      {delivery
                        ? formatQuantityWithUnit(delivery.beauftragte_menge, synced.einheit)
                        : formatQuantityWithUnit(synced.menge, synced.einheit)}
                    </td>
                    <td>
                      <strong>{title}</strong>
                      {positionsartBadge ? (
                        <span
                          className={
                            excludedFromTotal
                              ? "gd-preview-table__positionsart gd-preview-table__positionsart--excluded"
                              : "gd-preview-table__positionsart gd-preview-table__positionsart--included"
                          }
                        >
                          {positionsartBadge}
                        </span>
                      ) : null}
                      <p className="gd-preview-table__desc">
                        <GdRichTextInline text={description} />
                      </p>
                      {showPrices && documentRabattLabel(synced, doc.waehrung) ? (
                        <p className="gd-preview-table__rabatt">
                          {documentRabattLabel(synced, doc.waehrung)}
                        </p>
                      ) : null}
                    </td>
                    {showPrices ? (
                      <>
                        <td className="gd-preview-table__num">
                          {formatCurrency(positionEpNetto(synced), doc.waehrung)}
                        </td>
                        <td className="gd-preview-table__num">
                          {excludedFromTotal ? (
                            <>
                              <span className="gd-preview-table__line-excluded">
                                {formatCurrency(lineTotals.zeilen_netto, doc.waehrung)}
                              </span>
                              <div className="gd-preview-table__line-excluded-hint">nicht in Summe</div>
                            </>
                          ) : (
                            formatCurrency(lineTotals.zeilen_netto, doc.waehrung)
                          )}
                        </td>
                      </>
                    ) : null}
                    {delivery ? (
                      <>
                        <td className="gd-preview-table__num">
                          {formatQuantityWithUnit(delivery.geliefert_menge, synced.einheit)}
                        </td>
                        <td className="gd-preview-table__num">
                          {delivery.rueckstand_menge > 0.0005
                            ? formatQuantityWithUnit(delivery.rueckstand_menge, synced.einheit)
                            : "—"}
                        </td>
                      </>
                    ) : null}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {positions.length === 0 ? (
          <p className="gd-preview-paper__empty">Noch keine Positionen im Dokument.</p>
        ) : null}

        {showPrices ? (
          <footer className="gd-preview-paper__totals">
            <div className="gd-preview-paper__totals-row">
              <span>Zwischensumme netto</span>
              <span>{formatCurrency(totals.summe_netto, doc.waehrung)}</span>
            </div>
            {taxGroups.map((g) => (
              <div key={g.rate} className="gd-preview-paper__totals-row">
                <span>MwSt. {g.rate}%</span>
                <span>{formatCurrency(g.tax, doc.waehrung)}</span>
              </div>
            ))}
            <div className="gd-preview-paper__totals-row gd-preview-paper__totals-row--grand">
              <span>Gesamt brutto</span>
              <span>{formatCurrency(totals.summe_brutto, doc.waehrung)}</span>
            </div>
          </footer>
        ) : null}

        {doc.dokument_typ === "rechnung" &&
        doc.zahlungsuebersicht &&
        Array.isArray(doc.zahlungsuebersicht.rows) &&
        doc.zahlungsuebersicht.rows.length > 0 ? (
          <section className="gd-preview-paper__zahlungsuebersicht">
            <h3>Rechnungs- und Zahlungsübersicht</h3>
            <table>
              <thead>
                <tr>
                  <th />
                  <th>Datum</th>
                  <th className="gd-preview-table__num">Netto</th>
                  <th className="gd-preview-table__num">MwSt</th>
                  <th className="gd-preview-table__num">Brutto</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {doc.zahlungsuebersicht.rows.map((row) => (
                  <tr key={`${row.dokument_id ?? row.label}-${row.datum ?? ""}`}>
                    <td>{row.label}</td>
                    <td>{row.datum || ""}</td>
                    <td className="gd-preview-table__num">
                      {formatCurrency(row.summe_netto, doc.waehrung)}
                    </td>
                    <td className="gd-preview-table__num">
                      {formatCurrency(row.summe_steuer, doc.waehrung)}
                    </td>
                    <td className="gd-preview-table__num">
                      {formatCurrency(row.summe_brutto, doc.waehrung)}
                    </td>
                    <td>{row.zahlungsstatus_label || row.zahlungsstatus || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="gd-preview-paper__zahlungsuebersicht-cta">
              <strong>{doc.zahlungsuebersicht.cta_text}</strong>
            </p>
          </section>
        ) : null}

        {resolvedSchlusstext ? (
          <GdRichTextParagraphs text={resolvedSchlusstext} className="gd-preview-paper__outro" />
        ) : null}
      </div>
    </aside>
  );
}
